import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { fromJS, List, Map } from 'immutable';
import { debounce } from 'underscore';

import AutoCompleteContainer from '../AutoComplete';
import {
  clearUsers,
  getUserSuggestionsByRole,
  getUserSuggestions,
  getWindwardAllTemplates,
} from '../../actions/popup/windwardReportsPopup';
import RunWindwardReportPopup from '../../components/Popup/RunWindwardReportPopup';
import config from '../../config';
import { showInformation } from '../../utils/MessagePopup';
import { usePopupContext } from '../../contexts/PopupContext';

const defaultSuggestions = fromJS({
  firstDealMaker: {
    suggestText: '',
    suggestId: null,
    suggests: List(),
  },
  secondDealMaker: {
    suggestText: '',
    suggestId: null,
    suggests: List(),
  },
  researchAnalyst: {
    suggestText: '',
    suggestId: null,
    suggests: List(),
  },
});

const RunWindwardReportPopupContainer = props => {
  const {
    popup,
    getModuleSuggestion,
    getAnalystSuggestions,
    getUserSuggestions,
    getWindwardAllTemplates,
  } = props;

  const { openPopup, closePopup, endPopupLoading } = usePopupContext();

  const [popupLoading, setPopupLoading] = useState(false);
  const [module, setModule] = useState({ suggestText: '', suggestId: null, suggests: List() });
  const [suggestionsParams, setSuggestionsParams] = useState(defaultSuggestions);
  const [templates, setTemplates] = useState(List());
  const [selectedTemplateType, setSelectedTemplateType] = useState('');
  const [templateParams, setTemplateParams] = useState(Map());
  const [allSearchFields, setAllSearchFields] = useState();

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleRunWindwardReport = useCallback(() => {
    const selectedUserId = module.suggestId;
    const templateParamsValues = Object.values(templateParams.toJS());
    const runWindward = popup.getIn(['props', 'callback']);
    const searchFields = allSearchFields.filter(item => item.templateType === selectedTemplateType)?.[0].searchFields;

    setPopupLoading(true);
    runWindward(selectedUserId, selectedTemplateType, templateParamsValues, searchFields);
  }, [module, selectedTemplateType, allSearchFields, templateParams, popup]);

  const handleModuleSuggestionsChange = useCallback(({ name, value }) => {
    setModule(prevModule => ({
      ...prevModule,
      [name]: value,
    }));
  }, []);

  const handleOpenSaveQueryPopup = useCallback(() => {
    const handleAddSaveQuery = popup.getIn(['props', 'onAddSaveQuery']);
    const templateParamsValues = Object.values(templateParams.toJS());
    const suggestionsParamsValues = suggestionsParams.toJS();

    openPopup('SaveQueryPopup', {
      onAddSaveQuery: name =>
        handleAddSaveQuery(
          {
            windward: {
              selectedUserId: module.suggestId,
              selectedUserText: module.suggestText,
              selectedTemplateType,
              templateParams: templateParamsValues,
              suggestsParams: suggestionsParamsValues,
            },
          },
          name,
          {
            afterSuccess: () => showInformation(openPopup, 'Query saved successfully.'),
            afterError: ({ responseBody }) => {
              endPopupLoading(responseBody);
            },
          },
        ),
    });
  }, [popup, templateParams, suggestionsParams, module, selectedTemplateType, openPopup, endPopupLoading]);

  const handleUserSuggestionsChange = useCallback((key, { name, value }) => {
    if (name === 'suggestId') {
      setTemplateParams(prevTemplateParams => prevTemplateParams.set(key, Map({ field: key, value })));
    }
    setSuggestionsParams(previousSuggestionsParams => previousSuggestionsParams.setIn([key, name], value));
  }, []);

  const fetchModuleSuggestions = useCallback(
    value => {
      getModuleSuggestion(value).then(response => {
        if (!response || !response.data) {
          return;
        }

        if (response.meta.pagination.currentPage === 1) {
          setModule(prevModule => ({
            ...prevModule,
            suggests: fromJS([...response.data]),
          }));
        } else {
          setModule(prevModule => ({
            ...prevModule,
            suggests: fromJS(prevModule.suggests.concat([...response.data])),
          }));
        }
      });
    },
    [getModuleSuggestion],
  );

  const handleGetModuleSuggestions = useMemo(() => debounce(fetchModuleSuggestions, 400), [
    fetchModuleSuggestions,
  ]);

  const fetchUserSuggestions = useCallback(
    (key, value) => {
      let find;

      switch (key) {
        case 'firstDealMaker':
          find = getAnalystSuggestions;
          break;

        case 'secondDealMaker':
          find = getUserSuggestions;
          break;

        case 'researchAnalyst':
          find = getUserSuggestions;
          break;

        default:
          return;
      }

      find(value).then(response => {
        if (!response || !response.data) {
          return;
        }

        if (response.meta.pagination.currentPage === 1) {
          setSuggestionsParams(previousSuggestionsParams =>
            previousSuggestionsParams.setIn([key, 'suggests'], fromJS([...response.data])),
          );
        } else {
          setSuggestionsParams(previousSuggestionsParams =>
            previousSuggestionsParams.setIn(
              [key, 'suggests'],
              fromJS(previousSuggestionsParams.getIn([key, 'suggests']).concat([...response.data])),
            ),
          );
        }
      });
    },
    [getAnalystSuggestions, getUserSuggestions],
  );

  const handleGetUserSuggestions = useMemo(() => debounce(fetchUserSuggestions, 400), [
    fetchUserSuggestions,
  ]);

  const handleUpdateCheckboxField = useCallback(({ target: { name, checked } }) => {
    if (checked === true) {
      setTemplateParams(prevTemplateParams => prevTemplateParams.set(name, Map({ field: name, value: checked })));
    } else {
      setTemplateParams(prevTemplateParams => prevTemplateParams.delete(name));
    }
  }, []);

  const handleUpdateRadioField = useCallback(({ target: { name, value } }) => {
    setTemplateParams(prevTemplateParams => prevTemplateParams.set(name, Map({ field: value, value: true })));
  }, []);

  const handleSelectReport = useCallback(({ target: { value } }) => {
    setSelectedTemplateType(value);
    setSuggestionsParams(defaultSuggestions);
    setTemplateParams(Map());
  }, []);

  const getTemplatesList = useCallback(() => {
    setPopupLoading(true);
    getWindwardAllTemplates().then(response => {
      setPopupLoading(false);

      if (!response || !response.data) {
        return;
      }

      setAllSearchFields(response.data);

      const filteredList = response.data.map(item => {
        const checkboxFields = [];
        const suggestedFields = [];
        const radioFields = [];
        const fields = Object.values(item.fields);

        fields.forEach(field => {
          switch (field.typeField) {
            case 'checkbox':
              checkboxFields.push({
                name: field.name,
                title: field.text,
              });
              break;

            case 'dropdown':
              suggestedFields.push({
                name: field.name,
                title: field.text,
              });
              break;

            case 'radiobutton':
              radioFields.push({
                name: field.name,
                title: field.text,
                values: field.values.map(value => ({
                  name: value.name,
                  title: value.text,
                })),
              });
              break;

            default:
              break;
          }
        });

        return {
          name: item.templateType,
          title: item.templateText,
          checkboxFields,
          suggestedFields,
          radioFields,
        };
      });

      const templatesList = fromJS(filteredList);
      const windward = popup.getIn(['props', 'windward']);

      if (templatesList && templatesList.size > 0) {
        setTemplates(templatesList);
        setSelectedTemplateType(windward ? windward.get('selectedTemplateType') : templatesList.get(0).get('name'));
      }

      if (windward) {
        handleRunWindwardReport();
      }
    });
  }, [getWindwardAllTemplates, popup, handleRunWindwardReport]);

  useEffect(() => {
    const windward = popup.getIn(['props', 'windward']);

    if (windward) {
      setModule({
        suggestId: windward.get('selectedUserId'),
        suggestText: windward.get('selectedUserText'),
      });
      setTemplateParams(fromJS(windward.get('templateParams')));
      setSuggestionsParams(fromJS(windward.get('suggestsParams')));
    }

    getTemplatesList();
  }, []);

  const getSuggest = key => (
    <AutoCompleteContainer
      idName="suggestId"
      idValue={suggestionsParams.getIn([key, 'suggestId'])}
      onChange={value => handleUserSuggestionsChange(key, value)}
      onFind={value => handleGetUserSuggestions(key, value)}
      suggestions={suggestionsParams.getIn([key, 'suggests'])}
      suggestionsName="suggests"
      value={suggestionsParams.getIn([key, 'suggestText'])}
      valueName="suggestText"
      suggestIfEmpty
    />
  );

  const moduleSuggestion = (
    <AutoCompleteContainer
      idName="suggestId"
      idValue={module.suggestId}
      onChange={value => handleModuleSuggestionsChange(value)}
      onFind={value => handleGetModuleSuggestions(value)}
      suggestions={module.suggests}
      suggestionsName="suggests"
      value={module.suggestText}
      valueName="suggestText"
      suggestIfEmpty
    />
  );

  const firstDealMaker = getSuggest('firstDealMaker');
  const secondDealMaker = getSuggest('secondDealMaker');
  const researchAnalyst = getSuggest('researchAnalyst');

  const moduleProps = {
    module: moduleSuggestion,
  };

  const fieldsProps = {
    firstDealMaker,
    secondDealMaker,
    researchAnalyst,
    onCheckboxFieldUpdate: handleUpdateCheckboxField,
    onRadioFieldUpdate: handleUpdateRadioField,
  };

  const initialValues = popup.getIn(['props', 'windward']);

  const windwardProps = {
    templates,
    templateParams,
    selectedTemplateType,
    onSelectReport: handleSelectReport,
    initialValues,
  };

  const extraProps = {
    popupLoading,
    onClose: handleClose,
    onRunWindwardReport: handleRunWindwardReport,
    onOpenSaveQueryPopup: popup.getIn(['props', 'onAddSaveQuery']) ? handleOpenSaveQueryPopup : undefined,
    isDisabledRunButton: module.suggestId === null || selectedTemplateType === '',
  };

  return <RunWindwardReportPopup {...extraProps} {...windwardProps} {...moduleProps} {...fieldsProps} />;
};

function mapStateToProps(state) {
  return {
    popupState: state.windwardReportsPopup,
  };
}

const mapDispatchToProps = dispatch => ({
  getModuleSuggestion: value => dispatch(getUserSuggestionsByRole(value, config.DIRECTOR)),
  getAnalystSuggestions: value => dispatch(getUserSuggestionsByRole(value, config.ANALYST_ASSOCIATE)),
  clearSuggestions: () => dispatch(clearUsers()),
  ...bindActionCreators({ getWindwardAllTemplates, getUserSuggestions }, dispatch),
});

export { RunWindwardReportPopupContainer };
export default connect(mapStateToProps, mapDispatchToProps)(memo(RunWindwardReportPopupContainer));
